import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  EditRounded,
  MailOutlineRounded,
  PersonRounded,
  ReceiptLongRounded,
  WorkspacePremiumRounded,
} from "@mui/icons-material";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { getUserInfo, updateUserProfile } from "../../services/User/Authentication";
import SubscriptionPanel from "./components/SubscriptionPanel";
import PaymentHistoryPanel from "./components/PaymentHistoryPanel";
import EditProfilePanel from "./components/EditProfilePanel";
import { formatDateTime } from "../../Utils/formatDateTime";

const PROFILE_TABS = [
  {
    id: "subscription",
    label: "Your Subscription",
    icon: WorkspacePremiumRounded,
  },
  {
    id: "payments",
    label: "Payment History",
    icon: ReceiptLongRounded,
  },
  {
    id: "edit",
    label: "Edit Profile",
    icon: EditRounded,
  },
];

function readStoredUser() {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return {};
  }

  try {
    return JSON.parse(storedUser);
  } catch {
    return {};
  }
}

function getDisplayName(user) {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  if (fullName) {
    return fullName;
  }

  return user?.name || user?.firstName || user?.email || "The FormGuru Member";
}

function formatDisplayDate(value, fallback) {
  return formatDateTime(value, fallback);
}

function formatAmount(value, fallback) {
  if (typeof value === "number") {
    return `A$${value.toFixed(2)}`;
  }

  if (typeof value === "string" && value.trim()) {
    return value.includes("A$") || value.includes("$") ? value : `A$${value}`;
  }

  return fallback;
}

function buildSubscription(user) {
  const rawSubscription = user?.subscription;
  const hasSub =
    (rawSubscription && Object.keys(rawSubscription).length > 0) ||
    user?.subscription_tier ||
    user?.plan_name;

  if (!hasSub) {
    return null;
  }

  return {
    planName:
      rawSubscription?.name ||
      rawSubscription?.plan_name ||
      user?.plan_name ||
      user?.subscription_tier ||
      "",
    status:
      rawSubscription?.status ||
      user?.subscription_status ||
      "",
    price:
      rawSubscription?.price_display ||
      rawSubscription?.display_price ||
      formatAmount(rawSubscription?.price === 0 ? 0 : rawSubscription?.price || user?.subscription_price, ""),
    startDate: formatDisplayDate(
      rawSubscription?.start_date ||
      rawSubscription?.startDate ||
      user?.subscription_start_date,
      ""
    ),
    expiryDate: formatDisplayDate(
      rawSubscription?.end_date ||
      rawSubscription?.expiry_date ||
      rawSubscription?.expiryDate ||
      user?.subscription_end_date,
      ""
    ),
    features:
      Array.isArray(rawSubscription?.features) && rawSubscription.features.length
        ? rawSubscription.features
        : [],
  };
}

function buildPaymentHistory(user) {
  const rawPayments = user?.payment_history || user?.payments;
  if (!Array.isArray(rawPayments) || rawPayments.length === 0) {
    return [];
  }

  return rawPayments.map((payment, index) => ({
    id: payment.id || payment.transaction_id || `${payment.date || payment.paid_at || "payment"}-${index}`,
    planName: payment.plan_name || payment.plan || payment.name || "Subscription",
    paidAt: formatDisplayDate(payment.paid_at || payment.date || payment.created_at, "Recent"),
    amount: formatAmount(payment.amount === 0 ? 0 : payment.amount || payment.total, ""),
    status: payment.status || "Success",
  }));
}

export default function ProfilePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(() => {
    return location.state?.profileActiveTab || "subscription";
  });

  useEffect(() => {
    if (location.state?.profileActiveTab !== activeTab) {
      navigate(location.pathname, {
        state: { ...location.state, profileActiveTab: activeTab },
        replace: true,
      });
    }
  }, [activeTab, location.state, navigate, location.pathname]);
  const [user, setUser] = useState(readStoredUser());
  const [formState, setFormState] = useState({
    firstName: "",
    lastName: "",
    email: "",
    location: "",
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
  });

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userInfo = await getUserInfo();
        const fullName = [userInfo.first_name, userInfo.last_name]
          .filter(Boolean)
          .join(" ")
          .trim();
        setUser((currentUser) => {
          const nextUser = {
            ...currentUser,
            ...userInfo,
            name: fullName || currentUser?.name || "",
          };
          localStorage.setItem("user", JSON.stringify(nextUser));
          if (nextUser.subscription) {
            localStorage.setItem("subscription", JSON.stringify(nextUser.subscription));
          } else {
            localStorage.removeItem("subscription");
          }
          return nextUser;
        });
      } catch (err) {
        console.error("Failed to load user info:", err);
      }
    };
    fetchUserInfo();
  }, []);

  useEffect(() => {
    setFormState({
      firstName: user?.first_name || user?.firstName || "",
      lastName: user?.last_name || user?.lastName || "",
      email: user?.email || "",
      location: user?.location || "",
    });
  }, [user]);

  const subscription = buildSubscription(user);
  const payments = buildPaymentHistory(user);
  const displayName = getDisplayName(user);

  const handleFieldChange = (field, value) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((current) => ({
        ...current,
        [field]: "",
      }));
    }
  };

  const handleReset = () => {
    setFormState({
      firstName: user?.first_name || user?.firstName || "",
      lastName: user?.last_name || user?.lastName || "",
      email: user?.email || "",
      location: user?.location || "",
    });
    setErrors({
      firstName: "",
      lastName: "",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const newErrors = {
      firstName: !formState.firstName.trim() ? "First name is required" : "",
      lastName: !formState.lastName.trim() ? "Last name is required" : "",
    };

    setErrors(newErrors);

    if (newErrors.firstName || newErrors.lastName) {
      if (newErrors.firstName && newErrors.lastName) {
        toast.error("First name and last name are required");
      } else if (newErrors.firstName) {
        toast.error(newErrors.firstName);
      } else {
        toast.error(newErrors.lastName);
      }
      return;
    }

    try {
      const payload = {
        first_name: formState.firstName.trim(),
        last_name: formState.lastName.trim(),
      };

      const updatedUser = await updateUserProfile(payload);
      const fullName = [updatedUser.first_name, updatedUser.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();

      const nextUser = {
        ...user,
        ...updatedUser,
        name: fullName || user?.name || "",
        location: formState.location,
      };

      localStorage.setItem("user", JSON.stringify(nextUser));
      if (nextUser.subscription) {
        localStorage.setItem("subscription", JSON.stringify(nextUser.subscription));
      } else {
        localStorage.removeItem("subscription");
      }
      setUser(nextUser);
      window.dispatchEvent(new Event("user-profile-updated"));
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error(error.error || "Failed to update profile");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)]">
      <Navbar />

      <div className="flex justify-center items-center xl:py-20 md:py-[60px] py-10 px-5">
        <main className="max-w-[1280px] w-full">
          <section className="grid lg:grid-cols-[minmax(0,1.3fr)_minmax(320px,420px)] gap-6 items-stretch grid-cols-1">
            <div className="max-w-[720px]">
              <p className="text-[var(--color-text-muted)] md:text-sm text-xs font-bold uppercase mb-4 tracking-[0.04em]">Account Center</p>
              <h1 className="xl:text-6xl md:text-5xl text-[40px] leading-[0.98] font-bold tracking-[-0.03em]">Manage your profile, plan and billing in one place.</h1>
              <p className="text-[var(--color-text-muted)] md:text-lg text-base leading-[1.65] mt-4">
                Review your current access, inspect payment history and update your
                personal details without leaving the dashboard flow.
              </p>
            </div>

            <div className="relative bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] flex gap-5 p-4 lg:min-h-[198px] min-h-0 md:flex-row flex-col md:items-center items-start">
              <div className="w-[72px] h-[72px] rounded-[var(--radius-md)] bg-[rgba(200,255,0,0.12)] border border-[rgba(200,255,0,0.25)] text-[var(--color-primary)] inline-flex items-center justify-center xl:text-[28px] md:text-2xl text-xl font-extrabold shrink-0">
                {(displayName || "U").trim().charAt(0).toUpperCase()}
              </div>
              <div className="flex flex-col gap-3">
                <h2 className="xl:text-[28px] md:text-2xl text-xl">{displayName}</h2>
                <div className="flex items-center gap-2 text-[var(--color-text-muted)] xl:text-base text-sm">
                  <MailOutlineRounded sx={{ fontSize: 18 }} />
                  <span>{user?.email || "No email available"}</span>
                </div>
                <div className="flex items-center gap-2 text-[var(--color-text-muted)] xl:text-base text-sm">
                  <PersonRounded sx={{ fontSize: 18 }} />
                  <span>{subscription ? subscription.planName : "No Active Subscription"}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="flex flex-wrap gap-3 mt-8">
            {PROFILE_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`appearance-none md:w-auto w-full rounded-[var(--radius-md)] py-4 px-5 inline-flex items-center justify-start gap-2 xl:text-base text-sm font-bold cursor-pointer transition-all duration-[180ms] ease-out hover:-translate-y-[1px] border ${isActive
                      ? "bg-[rgba(200,255,0,0.12)] border-[rgba(200,255,0,0.25)] text-[var(--color-primary)]"
                      : "bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[rgba(200,255,0,0.25)] hover:text-[var(--color-text)]"
                    }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon sx={{ fontSize: 20 }} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </section>

          {activeTab === "subscription" && (
            <SubscriptionPanel subscription={subscription} />
          )}
          {activeTab === "payments" && (
            <PaymentHistoryPanel payments={payments} />
          )}
          {activeTab === "edit" && (
            <EditProfilePanel
              formState={formState}
              errors={errors}
              onChange={handleFieldChange}
              onSubmit={handleSubmit}
              onReset={handleReset}
            />
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
}
